import React, { useState } from 'react';
import { Configure, InstantSearch, useHits, useInstantSearch } from 'react-instantsearch-hooks-web';
import classnames from 'classnames';
import { useIsMobile, useIsTablet } from '@guildeducationinc/catalog-tools';
import { MxButton } from '@guildeducationinc/playground-mx';
import { Dialog, Typography } from '@guildeducationinc/recess';
import FilterListIcon from '@guildeducationinc/recess/icons/FilterListIcon';
import { FilterPane } from 'src/components/FilterPane';
import { useChatDisplay } from 'src/components/Providers/ChatDisplayProvider';
import { FacetsInputTagList } from '@src/components/FacetsInputTagList';
import { algoliaClient } from 'src/clients/Algolia';
import { HITS_PER_PAGE, onStateChange } from 'src/components/Providers/SearchProvider/helpers';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import styles from './MobileFilters.module.scss';

const ModalContent = ({ isMobile }: { isMobile: boolean }) => {
	const { filtersString, index } = useSearchContext();
	const { uiState: rootUiState, setUiState: setRootUiState } = useInstantSearch();
	const handleStateChange = ({ uiState, setUiState }) => {
		const newUiState = onStateChange({ uiState, setUiState, index });
		setUiState(uiState);
		setRootUiState(newUiState);
	};

	/**
	 * https://github.com/algolia/instantsearch/issues/5240
	 */
	return (
		<InstantSearch
			indexName={index}
			initialUiState={rootUiState}
			onStateChange={handleStateChange}
			searchClient={algoliaClient}
		>
			<Configure
				clickAnalytics={true}
				filters={filtersString}
				getRankingInfo={true}
				hitsPerPage={HITS_PER_PAGE}
			/>
			<div className={classnames(styles.modalBody, isMobile ? styles.modalBodyMobile : '')}>
				<FacetsInputTagList />
				<div className={classnames(styles.modalFilterPane, styles.modalFilterPaneMobile)}>
					<FilterPane />
				</div>
			</div>
		</InstantSearch>
	);
};

export const MobileFilters: React.FC = () => {
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const { setHideChat } = useChatDisplay();
	const { results } = useHits();
	const count = results.nbHits;

	const isMobile = useIsMobile();
	const isTablet = useIsTablet();

	if (mobileFiltersOpen) {
		setHideChat(true);
	}

	return (
		<div className={styles.filterCta}>
			<div className={styles.filterTextWrapper}>
				<Typography className={styles.filterText} variant='body1'>
					Select filters to get more personalized results.
				</Typography>
			</div>
			<MxButton
				appearance='outline'
				className={styles.filterButton}
				icon={<FilterListIcon size={24} />}
				iconDirection='right'
				onClick={() => {
					setMobileFiltersOpen(true);
				}}
				testid='mobile-filter-button'
			>
				View filters
			</MxButton>
			<Dialog
				buttonA={{
					style: {
						marginLeft: 'auto',
						width: isMobile ? '100%' : 'unset',
						display: isTablet ? 'flex' : 'block',
					},
					text: `View ${count} results`,
					onClick: () => {
						setMobileFiltersOpen(false);
						setHideChat(false);
					},
				}}
				content={<ModalContent isMobile={isMobile} />}
				fullScreen={isMobile}
				isOpen={mobileFiltersOpen}
				onClose={() => {
					setMobileFiltersOpen(false);
					setHideChat(false);
				}}
				size={isTablet ? 'sm' : 'lg'}
				title='Filter all programs'
			/>
		</div>
	);
};
